import { forwardRef, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { v4 } from 'uuid';

export const SectionHeader = styled.div`
    color: ${tokens.text.primary};
    font-weight: 500;
    margin-block: 1rem;
`;

export const Section = styled.section<{ $showBorder?: boolean }>`
    & + & {
        margin-top: 1rem;
        ${({ $showBorder }) =>
            $showBorder && `border-top: 1px solid ${tokens.border.default};`}
    }
    &:last-of-type {
        margin-bottom: 1rem;
    }
    padding: 0 1rem;
`;

export type PaneSectionProps = Readonly<{
    /**
     * The section label.
     */
    label?: ReactNode;
    /**
     * The section content.
     */
    children?: ReactNode;
    /**
     * ARIA text label for this element.
     */
    'aria-label'?: string;
    /**
     * Optional class name for the section.
     */
    className?: string;
    /**
     * Whether to show a border between this section and the next.
     */
    noSeparator?: boolean;
    /**
     * Optional component to render as the section header.
     */
    SectionHeaderComponent?: React.ElementType;
}>;

const PaneSection = forwardRef<HTMLDivElement, PaneSectionProps>(
    (
        {
            label,
            children,
            'aria-label': ariaLabel,
            className,
            noSeparator,
            SectionHeaderComponent = SectionHeader,
        },
        ref,
    ) => {
        const [id] = useState(() => `pane-section-header-${v4()}`);
        return (
            <Section
                ref={ref}
                aria-label={ariaLabel}
                className={className}
                aria-labelledby={id}
                $showBorder={!noSeparator}
            >
                {label && (
                    <SectionHeaderComponent id={id}>{label}</SectionHeaderComponent>
                )}
                <div data-testid="pane-section-content">{children}</div>
            </Section>
        );
    },
);

/**
 * Component for rendering a section within a `CollapsiblePane` component.
 */
export { PaneSection };
